import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import InsightCard from "./InsightCard";

interface RelatedArticlesProps {
  currentArticleId: string;
  categoryId: string | null;
}

type Article = {
  id: string;
  slug: string;
  slug_es: string;
  slug_en: string;
  article_type: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  cover_image_url: string | null;
  published_at: string;
  category_id: string | null;
};

type Category = {
  id: string;
  slug: string;
  name_es: string;
  name_en: string;
  color: string;
};

const RelatedArticles = ({ currentArticleId, categoryId }: RelatedArticlesProps) => {
  const { lang } = useLanguage();
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Record<string, Category>>({});

  useEffect(() => {
    const fetch = async () => {
      // Get articles from same category first, then fill with recent
      let query = supabase
        .from("blog_articles_public")
        .select("id, slug, slug_es, slug_en, article_type, title_es, title_en, excerpt_es, excerpt_en, cover_image_url, published_at, category_id")
        .neq("id", currentArticleId)
        .order("published_at", { ascending: false })
        .limit(6);

      if (categoryId) {
        query = query.eq("category_id", categoryId);
      }

      const { data } = await query;
      let result = (data as Article[]) || [];

      // If fewer than 3, backfill with any recent
      if (result.length < 3 && categoryId) {
        const ids = result.map((a) => a.id);
        ids.push(currentArticleId);
        const { data: more } = await supabase
          .from("blog_articles_public")
          .select("id, slug, slug_es, slug_en, article_type, title_es, title_en, excerpt_es, excerpt_en, cover_image_url, published_at, category_id")
          .not("id", "in", `(${ids.join(",")})`)
          .order("published_at", { ascending: false })
          .limit(3 - result.length);
        result = [...result, ...((more as Article[]) || [])];
      }

      setArticles(result.slice(0, 3));

      // Fetch categories
      const { data: cats } = await supabase.from("blog_categories").select("*");
      if (cats) {
        setCategories(Object.fromEntries(cats.map((c: any) => [c.id, c])));
      }
    };
    fetch();
  }, [currentArticleId, categoryId]);

  if (articles.length === 0) return null;

  return (
    <section aria-labelledby="related-heading" className="mt-16 pt-12 border-t border-border">
      <h2 id="related-heading" className="font-display text-2xl md:text-3xl font-bold text-foreground mb-8">
        {lang === "es" ? "Artículos relacionados" : "Related articles"}
      </h2>
      <div className="grid md:grid-cols-3 gap-6">
        {articles.map((article) => {
          const cat = article.category_id ? categories[article.category_id] : null;
          return (
            <InsightCard
              key={article.id}
              slug={article.slug}
              slugEs={article.slug_es}
              slugEn={article.slug_en}
              titleEs={article.title_es}
              titleEn={article.title_en}
              excerptEs={article.excerpt_es}
              excerptEn={article.excerpt_en}
              coverImage={article.cover_image_url}
              coverImageAlt={(article as any).cover_image_alt || undefined}
              categoryName={cat ? (lang === "es" ? cat.name_es : cat.name_en) : undefined}
              categoryColor={cat?.color}
              publishedAt={article.published_at}
              articleType={article.article_type as "blog" | "guide" | "premium"}
            />
          );
        })}
      </div>
    </section>
  );
};

export default RelatedArticles;
